import React, { useState, useEffect, useCallback, useRef } from 'react'
import { Outlet, NavLink, Link } from 'react-router-dom'
import { Activity, Users, BarChart3, Bell, ClipboardList, LogOut } from 'lucide-react'
import { mdApi } from '../api/mdApi'
import NotificationsPanel from './NotificationsPanel'
import { useAuth } from '../context/AuthContext'
import './Layout.css'

/**
 * Layout Component
 *
 * Main layout wrapper for the MD Portal application.
 * Includes header with navigation, notification bell, and footer.
 * Clinical Insights is now the first navigation item.
 */
function Layout() {
  const { role, logout } = useAuth()
  const isClinician = role === 'clinician'

  // State for notifications
  const [notificationCount, setNotificationCount] = useState(0)
  const [hasUrgent, setHasUrgent] = useState(false)
  const [isPanelOpen, setIsPanelOpen] = useState(false)
  
  // Use ref for lastChecked to avoid re-render loops
  // Refs don't cause re-renders when updated
  const lastCheckedRef = useRef(null)
  
  /**
   * Fetch notification counts from API
   */
  const fetchNotificationCounts = useCallback(async () => {
    try {
      const counts = await mdApi.getNotificationCounts()
      setNotificationCount(counts.total || 0)
      setHasUrgent(counts.urgentConcerns > 0)
      lastCheckedRef.current = new Date()
    } catch (err) {
      console.error('Error fetching notification counts:', err)
    }
  }, [])
  
  // Initial fetch and refresh on focus (runs only once on mount)
  useEffect(() => {
    fetchNotificationCounts()
    
    // Refresh counts when window gains focus
    const handleFocus = () => {
      const now = new Date()
      const lastChecked = lastCheckedRef.current
      // Only refresh if more than 1 minute since last check
      if (!lastChecked || (now - lastChecked) > 60000) {
        fetchNotificationCounts()
      }
    }
    
    window.addEventListener('focus', handleFocus)
    return () => window.removeEventListener('focus', handleFocus)
  }, [fetchNotificationCounts])
  
  // Use ref to track previous panel state to detect close action
  const prevIsPanelOpenRef = useRef(isPanelOpen)
  
  // Refresh counts when panel closes
  useEffect(() => {
    const wasOpen = prevIsPanelOpenRef.current
    prevIsPanelOpenRef.current = isPanelOpen
    
    // Only fetch if panel was open and is now closed
    if (wasOpen && !isPanelOpen) {
      // Small delay to allow any resolution actions to complete
      const timer = setTimeout(fetchNotificationCounts, 500)
      return () => clearTimeout(timer)
    }
  }, [isPanelOpen, fetchNotificationCounts])
  
  /**
   * Toggle notifications panel
   */
  const handleBellClick = () => {
    setIsPanelOpen(prev => !prev)
  }
  
  /**
   * Close notifications panel
   */
  const handleClosePanel = () => {
    setIsPanelOpen(false)
  }
  
  return (
    <div className="layout">
      <header className="layout-header">
        <div className="header-content">
          <Link to="/dashboard" className="header-logo-link" aria-label="Go to dashboard">
            <div className="header-logo">
              <Activity className="logo-icon" size={32} />
              <div>
                <h1 className="header-title">Lucena Health</h1>
                <p className="header-subtitle">Caregiver Dashboard</p>
              </div>
            </div>
          </Link>
          
          {/* Navigation - clinician-only views hidden for caregivers */}
          <nav className="header-nav">
            {isClinician && (
              <NavLink
                to="/clinical-insights"
                className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
              >
                <ClipboardList size={20} />
                <span>Clinical Insights</span>
              </NavLink>
            )}
            <NavLink
              to="/dashboard"
              className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
            >
              <Users size={20} />
              <span>Patients</span>
            </NavLink>
            {isClinician && (
              <NavLink
                to="/analytics"
                className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
              >
                <BarChart3 size={20} />
                <span>Analytics</span>
              </NavLink>
            )}
          </nav>

          {/* Notification Bell with Dropdown */}
          <div className="header-actions">
            <div className="notification-bell-container">
              <button
                className={`icon-button notification-bell-button ${hasUrgent ? 'has-urgent' : ''}`}
                title="Notifications"
                onClick={handleBellClick}
                aria-label={`Notifications: ${notificationCount} pending items`}
                aria-expanded={isPanelOpen}
              >
                <Bell size={20} />
                {notificationCount > 0 && (
                  <span className={`notification-badge ${hasUrgent ? 'urgent' : ''}`}>
                    {notificationCount > 99 ? '99+' : notificationCount}
                  </span>
                )}
              </button>
              
              <NotificationsPanel
                isOpen={isPanelOpen}
                onClose={handleClosePanel}
              />
            </div>
            <button
              className="icon-button"
              title="Sign out"
              aria-label="Sign out"
              onClick={logout}
            >
              <LogOut size={20} />
            </button>
          </div>
        </div>
      </header>

      <main className="layout-main">
        <Outlet />
      </main>

      <footer className="layout-footer">
                <p>© 2024 Lucena Health. Caregiver Dashboard v1.0.0</p>
      </footer>
    </div>
  )
}

export default Layout